import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, authHeaders } from "@/lib/api";
import { Trash2, Plus, Copy } from "lucide-react";

export default function CodesManager() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCode, setNewCode] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const res = await api.get("/access-codes", { headers: authHeaders() });
      setCodes(res.data);
    } catch {
      toast.error("Failed to load codes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCodes();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCode.trim()) return;
    setCreating(true);
    try {
      const res = await api.post(
        "/access-codes",
        { code: newCode.trim(), label: newLabel.trim() || null },
        { headers: authHeaders() }
      );
      setCodes((prev) => [...prev, res.data]);
      setNewCode("");
      setNewLabel("");
      toast.success(`Code "${res.data.code}" created.`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to create code.";
      toast.error(typeof msg === "string" ? msg : "Failed to create code.");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, code) => {
    try {
      await api.delete(`/access-codes/${id}`, { headers: authHeaders() });
      setCodes((prev) => prev.filter((c) => c.id !== id));
      toast.success(`Code "${code}" removed.`);
    } catch {
      toast.error("Failed to delete code.");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`Copied "${code}" to clipboard.`);
  };

  return (
    <section
      className="border p-6 sm:p-8 mb-12"
      style={{
        background: "rgba(48, 23, 40,0.55)",
        borderColor: "rgba(221, 191, 207,0.2)",
      }}
      data-testid="codes-manager"
    >
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="small-caps mb-2" style={{ color: "#B9929D" }}>
            · Returning-guest codes
          </div>
          <h2
            className="font-display"
            style={{ fontSize: "32px", color: "#F2EEE7", lineHeight: 1.1 }}
          >
            Instant-<em style={{ color: "#DDBFCF" }}>confirm</em> passes
          </h2>
        </div>
        <span className="text-xs italic" style={{ color: "#DDBFCF", opacity: 0.6 }}>
          Anyone entering these on the landing page is confirmed instantly.
        </span>
      </div>

      {/* Existing codes */}
      {loading ? (
        <p className="italic" style={{ color: "#DDBFCF", opacity: 0.7 }}>Loading…</p>
      ) : codes.length === 0 ? (
        <p className="italic" style={{ color: "#DDBFCF", opacity: 0.7 }}>
          No codes yet. Add the first one below.
        </p>
      ) : (
        <ul className="space-y-2 mb-8" data-testid="codes-list">
          {codes.map((c) => (
            <li
              key={c.id}
              className="flex items-center justify-between gap-4 py-3 border-b"
              style={{ borderColor: "rgba(221, 191, 207,0.12)" }}
              data-testid={`code-row-${c.code}`}
            >
              <div className="flex items-baseline gap-4 flex-wrap">
                <code
                  className="font-mono-body px-3 py-1 border"
                  style={{
                    color: "#F2EEE7",
                    borderColor: "rgba(185, 146, 157,0.4)",
                    background: "rgba(185, 146, 157,0.08)",
                    fontSize: "13px",
                    letterSpacing: "0.05em",
                  }}
                >
                  {c.code}
                </code>
                {c.label && (
                  <span style={{ color: "#F2EEE7", opacity: 0.75, fontSize: "13px" }}>
                    {c.label}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => copyCode(c.code)}
                  data-testid={`copy-code-${c.code}`}
                  className="p-2 opacity-60 hover:opacity-100 transition-opacity"
                  style={{ color: "#DDBFCF" }}
                  title="Copy"
                >
                  <Copy size={14} />
                </button>
                <button
                  onClick={() => handleDelete(c.id, c.code)}
                  data-testid={`delete-code-${c.code}`}
                  className="p-2 opacity-60 hover:opacity-100 transition-opacity"
                  style={{ color: "#f0b0a8" }}
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Create form */}
      <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-4 items-end">
        <div>
          <label className="block small-caps mb-2" style={{ color: "#B9929D" }} htmlFor="new-code">
            · New code
          </label>
          <input
            id="new-code"
            data-testid="new-code-input"
            className="kynn-input"
            placeholder="e.g. founders-2026"
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            autoCapitalize="off"
            autoCorrect="off"
          />
        </div>
        <div>
          <label className="block small-caps mb-2" style={{ color: "#B9929D" }} htmlFor="new-label">
            · Label <span style={{ opacity: 0.5 }}>(optional)</span>
          </label>
          <input
            id="new-label"
            data-testid="new-code-label"
            className="kynn-input"
            placeholder="Who is this for?"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
          />
        </div>
        <button
          type="submit"
          disabled={creating || !newCode.trim()}
          data-testid="create-code-btn"
          className="btn-primary"
        >
          <Plus size={14} /> {creating ? "Adding…" : "Add code"}
        </button>
      </form>
    </section>
  );
}
